import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../config/supabase';

/**
 * Hook for subscribing to Supabase Realtime postgres_changes.
 *
 * @param {object} options
 * @param {string} options.table      - Table name to listen on
 * @param {string} [options.event]    - 'INSERT' | 'UPDATE' | 'DELETE' | '*' (default '*')
 * @param {string} [options.schema]   - Schema name (default 'public')
 * @param {string} [options.filter]   - Row-level filter, e.g. "user_id=eq.<uuid>"
 * @param {boolean} [options.enabled] - Whether the subscription is active (default true)
 * @param {function} options.onInsert - Callback for INSERT events
 * @param {function} options.onUpdate - Callback for UPDATE events
 * @param {function} options.onDelete - Callback for DELETE events
 * @param {function} options.onChange - Catch-all callback for any event
 */
export function useRealtimeSubscription({
  table,
  event = '*',
  schema = 'public',
  filter,
  enabled = true,
  onInsert,
  onUpdate,
  onDelete,
  onChange,
}) {
  const channelRef = useRef(null);

  // Stable callback refs to avoid re-subscribing on every render
  const onInsertRef = useRef(onInsert);
  const onUpdateRef = useRef(onUpdate);
  const onDeleteRef = useRef(onDelete);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onInsertRef.current = onInsert;
    onUpdateRef.current = onUpdate;
    onDeleteRef.current = onDelete;
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    if (!enabled || !table) return;

    const channelName = `realtime:${table}:${filter || 'all'}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event,
          schema,
          table,
          ...(filter ? { filter } : {}),
        },
        (payload) => {
          // Route to specific callbacks
          if (payload.eventType === 'INSERT' && onInsertRef.current) {
            onInsertRef.current(payload.new, payload);
          }
          if (payload.eventType === 'UPDATE' && onUpdateRef.current) {
            onUpdateRef.current(payload.new, payload.old, payload);
          }
          if (payload.eventType === 'DELETE' && onDeleteRef.current) {
            onDeleteRef.current(payload.old, payload);
          }
          // Always call onChange if provided
          if (onChangeRef.current) {
            onChangeRef.current(payload);
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [table, event, schema, filter, enabled]);

  const unsubscribe = useCallback(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  return { unsubscribe };
}

export default useRealtimeSubscription;
